import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit2, Trash2, Calendar, MapPin, Users, FileText, Clock } from 'lucide-react';
import { Meeting } from '../types';
import { showGlobalNotification } from '../hooks/useNotifications';

const MeetingsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState<Meeting | null>(null);
  const [meetingsData, setMeetingsData] = useState<Meeting[]>([]);
  const [usersData, setUsersData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming');
  const [selectedAttendees, setSelectedAttendees] = useState<string[]>([]);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  
  const [meetingFormData, setMeetingFormData] = useState({
    title: '',
    meeting_date: '',
    meeting_time: '',
    location: '',
    attendees: '',
    agenda: '',
    notes: '',
    decisions: '',
    action_items: '',
    status: 'planned' as Meeting['status']
  });

  useEffect(() => {
    loadMeetings();
  }, []);

  const loadMeetings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const [meetingsResponse, usersResponse] = await Promise.all([
        fetch('/api/meetings', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch('/api/users', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (meetingsResponse.ok) {
        const meetingsData = await meetingsResponse.json();
        setMeetingsData(meetingsData);
      }
      
      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        setUsersData(usersData.filter((user: any) => 
          user.is_active && (user.role === 'admin' || user.role === 'superadmin')
        ));
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showGlobalNotification.error('Hata', 'Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingMeeting(null);
    setSelectedAttendees([]);
    setMeetingFormData({
      title: '',
      meeting_date: '',
      meeting_time: '',
      location: '',
      attendees: '',
      agenda: '',
      notes: '',
      decisions: '',
      action_items: '',
      status: 'planned'
    });
    setShowModal(true);
  };

  const handleEdit = (meeting: Meeting) => {
    setEditingMeeting(meeting);
    const attendeeNames = meeting.attendees ? meeting.attendees.split(', ') : [];
    setSelectedAttendees(attendeeNames);
    
    // Format date for input[type="date"] - ensure YYYY-MM-DD format
    const formattedDate = meeting.meeting_date.includes('T') 
      ? meeting.meeting_date.split('T')[0] 
      : meeting.meeting_date;
    
    setMeetingFormData({
      title: meeting.title,
      meeting_date: formattedDate,
      meeting_time: meeting.meeting_time,
      location: meeting.location || '',
      attendees: meeting.attendees || '',
      agenda: meeting.agenda || '',
      notes: meeting.notes || '',
      decisions: meeting.decisions || '',
      action_items: meeting.action_items || '',
      status: meeting.status
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!meetingFormData.title || !meetingFormData.meeting_date || !meetingFormData.meeting_time) {
      showGlobalNotification.error('Hata', 'Zorunlu alanları doldurunuz');
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const url = editingMeeting ? `/api/meetings/${editingMeeting.id}` : '/api/meetings';
      const method = editingMeeting ? 'PUT' : 'POST';
      
      const dataToSend = {
        ...meetingFormData,
        attendees: selectedAttendees.join(', ')
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSend)
      });

      if (response.ok) {
        await loadMeetings();
        setShowModal(false);
        const message = editingMeeting ? 'Toplantı güncellendi' : 'Toplantı oluşturuldu';
        showGlobalNotification.success('Başarılı', message);
        
        // Create notification
        const token = localStorage.getItem('auth_token');
        await fetch('/api/notifications', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            title: editingMeeting ? 'Toplantı Güncellendi' : 'Yeni Toplantı',
            message: `"${dataToSend.title}" toplantısı ${editingMeeting ? 'güncellendi' : 'oluşturuldu'}`,
            type: 'success',
            category: 'meeting',
            priority: 'normal',
            action_url: '/meetings',
            action_text: 'Toplantıları Görüntüle'
          })
        });
      } else {
        const error = await response.json();
        showGlobalNotification.error('Hata', error.message || 'İşlem başarısız');
      }
    } catch (error) {
      console.error('Error saving meeting:', error);
      showGlobalNotification.error('Hata', 'Toplantı kaydedilirken hata oluştu');
    }
  };

  const handleDelete = async (id: number, title: string) => {
    if (confirm(`"${title}" toplantısını silmek istediğinizden emin misiniz?`)) {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`/api/meetings/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          await loadMeetings();
          showGlobalNotification.success('Başarılı', 'Toplantı silindi');
          
          // Create notification
          const token = localStorage.getItem('auth_token');
          await fetch('/api/notifications', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              title: 'Toplantı Silindi',
              message: `"${title}" toplantısı silindi`,
              type: 'warning',
              category: 'meeting',
              priority: 'normal',
              action_url: '/meetings',
              action_text: 'Toplantıları Görüntüle'
            })
          });
        }
      } catch (error) {
        console.error('Error deleting meeting:', error);
        showGlobalNotification.error('Hata', 'Toplantı silinirken hata oluştu');
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'planned': return 'bg-primary/20 text-primary-dark';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'planned': return 'Planlandı';
      case 'completed': return 'Tamamlandı';
      case 'cancelled': return 'İptal Edildi';
      default: return 'Bilinmiyor';
    }
  };

  const createWeeklyMeetings = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/meetings/create-weekly', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        await loadMeetings();
        showGlobalNotification.success('Başarılı', `${result.created} haftalık toplantı oluşturuldu`);
      }
    } catch (error) {
      console.error('Error creating weekly meetings:', error);
      showGlobalNotification.error('Hata', 'Haftalık toplantılar oluşturulurken hata oluştu');
    }
  };
  
  const updateAttendees = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/meetings/update-attendees', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        await loadMeetings();
        showGlobalNotification.success('Başarılı', `${result.updated} toplantının katılımcıları güncellendi`);
      }
    } catch (error) {
      console.error('Error updating attendees:', error);
      showGlobalNotification.error('Hata', 'Katılımcılar güncellenirken hata oluştu');
    }
  };

  const today = new Date().toISOString().split('T')[0];
  
  const upcomingMeetings = meetingsData.filter(meeting => 
    meeting.meeting_date >= today &&
    (String(meeting.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
     meeting.String(location || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
     meeting.String(attendees || '').toLowerCase().includes(searchTerm.toLowerCase()))
  ).sort((a, b) => {
    const dateA = new Date(`${a.meeting_date} ${a.meeting_time}`);
    const dateB = new Date(`${b.meeting_date} ${b.meeting_time}`);
    return dateA.getTime() - dateB.getTime(); // En yakın tarih en üstte
  });
  
  const pastMeetings = meetingsData.filter(meeting => 
    meeting.meeting_date < today &&
    (String(meeting.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
     meeting.String(location || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
     meeting.String(attendees || '').toLowerCase().includes(searchTerm.toLowerCase()))
  ).sort((a, b) => {
    const dateA = new Date(`${a.meeting_date} ${a.meeting_time}`);
    const dateB = new Date(`${b.meeting_date} ${b.meeting_time}`);
    return dateB.getTime() - dateA.getTime(); // En son tarih en üstte
  });
  
  const filteredMeetings = activeTab === 'upcoming' ? upcomingMeetings : pastMeetings;

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Toplantı Yönetimi</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">Toplantı notlarını ve kararları yönetin</p>
        </div>
        <div className="grid grid-cols-1 sm:flex sm:space-x-3 gap-2 w-full sm:w-auto">
          <button
            onClick={createWeeklyMeetings}
            className="flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors min-h-[44px] text-sm"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Haftalık Toplantılar
          </button>
          <button
            onClick={handleAdd}
            className="flex items-center justify-center px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors min-h-[44px] text-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            Yeni Toplantı
          </button>
        </div>
      </div>

      <div className="mb-6 space-y-4">
        <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'upcoming'
                ? 'bg-white dark:bg-gray-800 text-primary shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300'
            }`}
          >
            Gelecek Toplantılar ({upcomingMeetings.length})
          </button>
          <button
            onClick={() => setActiveTab('past')}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'past'
                ? 'bg-white dark:bg-gray-800 text-primary shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300'
            }`}
          >
            Geçmiş Toplantılar ({pastMeetings.length})
          </button>
        </div>
        
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Toplantı ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent w-full min-h-[44px]"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {filteredMeetings.map((meeting) => (
          <div key={meeting.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 hover:shadow-md transition-shadow">
            <div className="flex flex-col sm:flex-row justify-between items-start mb-4 gap-3">
              <div className="flex items-center flex-1 min-w-0">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                </div>
                <div className="ml-3 min-w-0 flex-1">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">{meeting.title}</h3>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                    {new Date(meeting.meeting_date).toLocaleDateString('tr-TR')} - {meeting.meeting_time}
                  </p>
                </div>
              </div>
              <div className="flex space-x-1 sm:space-x-2">
                <button
                  onClick={() => handleEdit(meeting)}
                  className="p-1 min-h-[32px] min-w-[32px] flex items-center justify-center text-gray-400 hover:text-primary transition-colors"
                >
                  <Edit2 className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
                <button
                  onClick={() => handleDelete(meeting.id, meeting.title)}
                  className="p-1 min-h-[32px] min-w-[32px] flex items-center justify-center text-gray-400 hover:text-red-600 transition-colors"
                >
                  <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
              </div>
            </div>

            <div className="space-y-2 sm:space-y-3">
              {meeting.location && (
                <div className="flex items-center text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  <MapPin className="w-3 h-3 sm:w-4 sm:h-4 mr-2 flex-shrink-0" />
                  <span className="truncate">{meeting.location}</span>
                </div>
              )}
              
              {meeting.attendees && (
                <div className="flex items-center text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-2 flex-shrink-0" />
                  <span className="truncate">{meeting.attendees}</span>
                </div>
              )}

              {meeting.agenda && (
                <div className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                  <div className="flex items-center mb-1">
                    <FileText className="w-3 h-3 sm:w-4 sm:h-4 mr-2 flex-shrink-0" />
                    <span className="font-medium">Gündem:</span>
                  </div>
                  <p className="ml-6 line-clamp-2">{meeting.agenda}</p>
                </div>
              )}

              {meeting.notes && (
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  <div className="flex items-center mb-1">
                    <FileText className="w-4 h-4 mr-2" />
                    <span className="font-medium">Notlar:</span>
                  </div>
                  <p className="ml-6 line-clamp-2">{meeting.notes}</p>
                </div>
              )}
            </div>

            <div className="flex justify-between items-center pt-4 border-t border-gray-100 mt-4">
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(meeting.status)}`}>
                {getStatusText(meeting.status)}
              </span>
              <span className="text-xs text-gray-400">
                {meeting.created_by_name}
              </span>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {editingMeeting ? 'Toplantı Düzenle' : 'Yeni Toplantı'}
              </h2>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Toplantı Başlığı *
                  </label>
                  <input
                    type="text"
                    value={meetingFormData.title}
                    onChange={(e) => setMeetingFormData({...meetingFormData, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Toplantı başlığı"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tarih *
                  </label>
                  <input
                    type="date"
                    value={meetingFormData.meeting_date}
                    onChange={(e) => setMeetingFormData({...meetingFormData, meeting_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Saat *
                  </label>
                  <input
                    type="time"
                    value={meetingFormData.meeting_time}
                    onChange={(e) => setMeetingFormData({...meetingFormData, meeting_time: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Konum
                  </label>
                  <select
                    value={meetingFormData.location}
                    onChange={(e) => setMeetingFormData({...meetingFormData, location: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">Konum seçin</option>
                    <option value="Microsoft Teams Meeting">Microsoft Teams Meeting</option>
                    <option value="Toplantı Salonu">Toplantı Salonu</option>
                    <option value="Diğer">Diğer</option>
                  </select>
                  {meetingFormData.location === 'Diğer' && (
                    <input
                      type="text"
                      placeholder="Özel konum girin"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent mt-2"
                      onChange={(e) => setMeetingFormData({...meetingFormData, location: e.target.value})}
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Durum
                  </label>
                  <select
                    value={meetingFormData.status}
                    onChange={(e) => setMeetingFormData({...meetingFormData, status: e.target.value as Meeting['status']})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="planned">Planlandı</option>
                    <option value="completed">Tamamlandı</option>
                    <option value="cancelled">İptal Edildi</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Katılımcılar
                  </label>
                  <div className="border border-gray-300 dark:border-gray-600 rounded-md p-2 min-h-[100px] max-h-[200px] overflow-y-auto">
                    {usersData.map((user) => (
                      <label key={user.id} className="flex items-center p-2 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedAttendees.includes(user.name)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedAttendees([...selectedAttendees, user.name]);
                            } else {
                              setSelectedAttendees(selectedAttendees.filter(name => name !== user.name));
                            }
                          }}
                          className="mr-2 text-primary focus:ring-primary"
                        />
                        <span className="text-sm">{user.name}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">({user.role})</span>
                      </label>
                    ))}
                  </div>
                  {selectedAttendees.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {selectedAttendees.map((attendee) => (
                        <span key={attendee} className="inline-flex items-center px-2 py-1 bg-primary/20 text-primary-dark text-xs rounded-full">
                          {attendee}
                          <button
                            type="button"
                            onClick={() => setSelectedAttendees(selectedAttendees.filter(name => name !== attendee))}
                            className="ml-1 text-primary hover:text-primary-dark"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Gündem
                  </label>
                  <textarea
                    value={meetingFormData.agenda}
                    onChange={(e) => setMeetingFormData({...meetingFormData, agenda: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Toplantı gündemi"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Notlar
                  </label>
                  <textarea
                    value={meetingFormData.notes}
                    onChange={(e) => setMeetingFormData({...meetingFormData, notes: e.target.value})}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Toplantı notları"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Kararlar
                  </label>
                  <textarea
                    value={meetingFormData.decisions}
                    onChange={(e) => setMeetingFormData({...meetingFormData, decisions: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Alınan kararlar"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Aksiyon Maddeleri
                  </label>
                  <textarea
                    value={meetingFormData.action_items}
                    onChange={(e) => setMeetingFormData({...meetingFormData, action_items: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Yapılacak işler ve sorumlular"
                  />
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
              >
                {editingMeeting ? 'Güncelle' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingsPage;